/* eslint-disable no-undef */
// Modules
import React, { useEffect, useMemo } from 'react'
import { Button, TextField } from '@dasa-health/components-react'
import { Flex, Spacing } from '@dasa-health/alma-react'
import { SystemBoldViewon, SystemBoldViewoff } from '@dasa-health/icons'

// Components
import { CustomButton, CustomForm } from './styles'

// Application
import { useCPFLoginForm } from 'redesign/hooks/useCPFLoginForm'
import { usePhasedLogin } from 'redesign/hooks/usePhasedLogin'
import { useCampaigns } from 'redesign/hooks/useCampaigns'
import { getQueryParamsValue } from 'utils'
import { useLocation } from 'react-router-dom'
import { useSnackbar } from 'redesign/hooks/useSnackbar'
import iconNav from 'assets/icon-nav.svg'
import iconNavDisabled from 'assets/icon-nav-disabled.svg'
import { useCurrentLaboratory } from 'redesign/hooks/useCurrentLaboratory'
import { WarningServiceIsDown } from '../WarningServiceIsDown'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const window: any

type CPFPhasedLoginFormABTestProps = {
    isServiceDown: boolean
}

export const CPFPhasedLoginFormABTest = ({ isServiceDown = false }: CPFPhasedLoginFormABTestProps) => {
    const { dispatchAnalytics, dispatchAnalyticsABTest } = usePhasedLogin()
    const { currentLaboratory } = useCurrentLaboratory()
    const {
        values,
        errors,
        touched,
        handleBlur,
        viewPassword,
        handleSubmit,
        handleChange,
        isSubmitting,
        handleChangeCpf,
        handleViewPassword,
        redirectToForgotPassword,
    } = useCPFLoginForm()
    const { changeCampaingSessionStorage } = useCampaigns()
    const location = useLocation()
    const { openSnackbar } = useSnackbar()

    useEffect(() => {
        dispatchAnalyticsABTest('new_login', currentLaboratory?.name)
        const step = values.showPasswordField ? 'insert_password' : 'insert_cpf'
        dispatchAnalytics(step)
    }, [dispatchAnalytics, dispatchAnalyticsABTest, values.showPasswordField, currentLaboratory])

    useEffect(() => {
        changeCampaingSessionStorage()
    }, [changeCampaingSessionStorage])

    useEffect(() => {
        const origin = getQueryParamsValue(location, 'origin')
        if (origin === 'sessao-expirada') {
            openSnackbar({
                variant: 'error',
                message: 'Sessão expirada. Insira seus dados novamente para acessar sua conta Nav Dasa.',
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const isDisableButton = useMemo(() => !values.cpf || (!values.showPasswordField && !!errors.cpf) || isSubmitting, [
        values,
        errors,
        isSubmitting,
    ])

    return (
        <>
            <CustomForm onSubmit={handleSubmit}>
                <Flex flexDirection="column" height="100%">
                    {isServiceDown && <WarningServiceIsDown />}
                    <Spacing height={isServiceDown ? 24 : 32} />
                    <TextField
                        autoComplete="username"
                        className="textInput"
                        helperText={touched.cpf ? errors.cpf : ''}
                        inputMode="numeric"
                        label="Digite o CPF para acessar ou cadastrar"
                        name="cpf"
                        onBlur={handleBlur}
                        onChange={handleChangeCpf}
                        readOnly={isSubmitting}
                        type="tel"
                        value={values.cpf}
                        variant={touched.cpf && errors.cpf ? 'error' : 'default'}
                    />
                    <Spacing height={56} />
                    {values.showPasswordField && (
                        <>
                            <TextField
                                className="textInput"
                                helperText={touched.password ? errors.password : ''}
                                label="Senha"
                                name="password"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                readOnly={isSubmitting}
                                trailingIcon={
                                    viewPassword ? (
                                        <SystemBoldViewoff data-testid="change-type-input" onClick={handleViewPassword} />
                                    ) : (
                                        <SystemBoldViewon data-testid="change-type-input" onClick={handleViewPassword} />
                                    )
                                }
                                type={viewPassword ? 'text' : 'password'}
                                value={values.password}
                                variant={touched.password && errors.password ? 'error' : 'default'}
                            />
                            <Spacing height={32} />
                            <Button
                                data-current-service="Login faseado"
                                data-item-position="Login CPF"
                                data-item-text="Esqueci a senha"
                                onClick={() => redirectToForgotPassword(values.cpf)}
                                size="compact"
                                type="button"
                                variant="tertiary"
                                width="100%"
                            >
                                Esqueci a senha
                            </Button>
                            <Spacing height={32} />
                        </>
                    )}

                    <Flex flexDirection="column" marginTop={'initial'}>
                        <CustomButton disabled={isDisableButton} isLoading={isSubmitting} width="100%">
                            Agendar <img alt="Logo" src={isDisableButton ? iconNavDisabled : iconNav} />
                        </CustomButton>
                    </Flex>
                </Flex>
            </CustomForm>
        </>
    )
}
